import { useState, type ReactNode } from 'react'
import { Compass, CreditCard, Truck } from 'lucide-react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { Row } from 'src/shared/components/Semantic/all'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { ONBOARDING_ELEMENT_IDS } from 'src/shared/hooks/useOnboarding'
import {
  getQuoteStatusDisplayLabel,
  getQuoteStatusPhaseIndex,
  QUOTE_STATUS_PHASES,
} from 'src/shared/consts/quoteStatus'
import { proofEntriesFromQuoteProofLinks } from 'src/shared/utils/proofUrls'
import { cn } from 'src/shared/components/ui/utils'
import { REQUEST_TAB_DEFINITIONS } from '../../consts'
import type { RequestTabId } from '../../types'
import { useRequestContext } from '../../hooks/RequestContext'
import { OrderProgressPopover } from '../RequestStatusIndicator'
import {
  StatusNavItem,
  type StatusNavPhaseState,
} from './components/StatusNavItem'

const TAB_PHASE_INDEX: Record<RequestTabId, number> = {
  explore: 0,
  checkout: 1,
  delivery: 2,
}

const TAB_ICONS: Record<RequestTabId, ReactNode> = {
  explore: <Compass className="h-4 w-4" strokeWidth={2.25} />,
  checkout: <CreditCard className="h-4 w-4" strokeWidth={2.25} />,
  delivery: <Truck className="h-4 w-4" strokeWidth={2.25} />,
}

const getPhaseState = (
  itemPhaseIndex: number,
  currentPhaseIndex: number
): StatusNavPhaseState => {
  if (currentPhaseIndex > itemPhaseIndex) return 'done'
  if (currentPhaseIndex === itemPhaseIndex) return 'current'
  return 'upcoming'
}

export const RequestBottomNav = ({
  activeTab,
  onTabChange,
}: {
  activeTab: RequestTabId
  onTabChange: (tab: RequestTabId) => void
}) => {
  const { t } = useLocalization()
  const { quote } = useRequestContext()
  const [popoverOpen, setPopoverOpen] = useState(false)

  const resolvedStatus = quote?.status
  const phaseIndex = getQuoteStatusPhaseIndex(resolvedStatus)
  const hasProofs =
    proofEntriesFromQuoteProofLinks(quote?.quote_proof).length > 0
  const detailLabel = t(
    getQuoteStatusDisplayLabel(resolvedStatus, { hasProofs })
  )
  const allDone = phaseIndex >= QUOTE_STATUS_PHASES.length

  const handleSegmentClick = (tabId: RequestTabId) => {
    if (activeTab === tabId) {
      setPopoverOpen((open) => !open)
      return
    }
    setPopoverOpen(false)
    onTabChange(tabId)
  }

  return (
    <Row className="pointer-events-none absolute bottom-4 left-1/2 z-50 max-w-[calc(100%-1.5rem)] -translate-x-1/2">
      <PopoverPrimitive.Root open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverPrimitive.Anchor asChild>
          <div
            id={ONBOARDING_ELEMENT_IDS.requestNav}
            className="pointer-events-auto flex max-w-full items-center gap-0.5 overflow-x-auto rounded-full border border-gray-200/80 bg-white/95 px-1.5 py-1.5 shadow-lg backdrop-blur-md"
          >
            {REQUEST_TAB_DEFINITIONS.map((tab, index) => {
              const itemPhaseIndex = TAB_PHASE_INDEX[tab.id]
              const phaseState = allDone
                ? 'done'
                : getPhaseState(itemPhaseIndex, phaseIndex)
              const selected = activeTab === tab.id
              const subLabel =
                phaseState === 'done'
                  ? t('Completed', 'Klar')
                  : phaseState === 'current'
                    ? detailLabel
                    : selected && tab.id === 'checkout'
                      ? t('Approve quote and proofs', 'Godkänn offert och korrekturer')
                      : selected && tab.id === 'delivery'
                        ? t('After production', 'Efter produktion')
                        : t('Upcoming', 'Kommande')

              return (
                <Row key={tab.id} className="shrink-0 items-center">
                  {index > 0 ? (
                    <div className="mx-0.5 h-10 w-px shrink-0 bg-gray-200/80" />
                  ) : null}
                  <StatusNavItem
                    label={t(tab.label, tab.labelSv)}
                    phaseState={phaseState}
                    selected={selected}
                    subLabel={subLabel}
                    icon={TAB_ICONS[tab.id]}
                    onClick={() => handleSegmentClick(tab.id)}
                  />
                </Row>
              )
            })}
          </div>
        </PopoverPrimitive.Anchor>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="center"
            side="top"
            sideOffset={10}
            className={cn(
              'z-50 w-[min(340px,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-gray-200/80 bg-white/95 p-0 text-popover-foreground shadow-xl backdrop-blur-md outline-none',
              'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2'
            )}
          >
            <OrderProgressPopover
              key={popoverOpen ? `${phaseIndex}-${resolvedStatus}` : 'closed'}
              currentPhaseIndex={phaseIndex}
              currentLabel={detailLabel}
            />
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </Row>
  )
}
